import api from "../utils/api";

const BASE = "/off-grid-calculator";
const ALL_BRANCHES = "__all__";

function normalizeBranch(branch) {
  if (!branch || branch === ALL_BRANCHES) return undefined;
  return branch;
}

function normalizeMachine(node) {
  const name = node?.name || node?.machine_name || "Unknown";
  const category = (node?.category || "").toUpperCase();
  return {
    name,
    machine_name: name,
    category,
    isMain: category === "GRID",
  };
}

function normalizeMain(node) {
  const name = node?.name || node?.machine_name || "Unknown";
  const match = name.match(/\bmain\s*(\d+)\b/i);
  const mainNumber = match ? Number(match[1]) : null;
  return {
    name,
    machine_name: name,
    branchName: node?.branchName || node?.branch_name || null,
    mainNumber,
    displayLabel: mainNumber ? `Main ${mainNumber}` : name,
    category: node?.category,
  };
}

function normalizeScopeResponse(raw) {
  const data = raw?.data ?? raw ?? {};
  return {
    dateRange: data.dateRange || null,
    branches: data.branches || [],
    machines: (data.machines || []).map(normalizeMachine),
    mains: (data.mains || []).map(normalizeMain),
    hasAnyMains: Boolean(data.hasAnyMains ?? data.mains?.length),
  };
}

function normalizeConstants(constants = {}) {
  const sizingCase = constants.sizingCase ?? constants.sizing_case ?? "worst";
  return {
    ...constants,
    systemEfficiency: constants.solarEfficiency ?? constants.systemEfficiency ?? 0.75,
    batteryDod: constants.batteryDod ?? 0.9,
    safetyMargin: constants.inverterMargin ?? constants.safetyMargin ?? 1.25,
    pshUsed: constants.pshUsed,
    monthUsed: constants.monthUsed,
    sizingCase,
  };
}

function normalizeSolarSystem(solarSystem = {}, constants = {}) {
  const psh = solarSystem.psh ?? solarSystem.pshUsed ?? constants.pshUsed ?? 0;
  return {
    ...solarSystem,
    psh,
    pshUsed: solarSystem.pshUsed ?? psh,
    monthUsed: solarSystem.monthUsed ?? constants.monthUsed,
    sizingCase: solarSystem.sizingCase ?? constants.sizingCase ?? "worst",
  };
}

function hasLoadData(loadAnalysis) {
  return Number(loadAnalysis?.totalDailyEnergyWh) > 0;
}

export function normalizeAnalyzeResult(entry, options = {}) {
  if (!entry) return null;

  const name = entry.name || entry.machine_name || options.fallbackName || "Unknown";
  const loadAnalysis = {
    ...entry.loadAnalysis,
    maxDailyKwh: (Number(entry.loadAnalysis?.totalDailyEnergyWh) || 0) / 1000,
  };
  const constants = normalizeConstants(entry.constants);
  const solarSystem = normalizeSolarSystem(entry.solarSystem, constants);

  return {
    name,
    machine_name: name,
    badge: entry.badge ?? options.badge ?? null,
    machineNames: entry.machineNames || entry.machine_names || [name],
    isCombined: Boolean(entry.isCombined ?? options.isCombined),
    loadAnalysis,
    solarSystem,
    constants,
    error: entry.error || null,
  };
}

function normalizeCombinedResult(combined, results = []) {
  if (!combined) return null;

  const machineNames =
    combined.machine_names ||
    combined.machineNames ||
    results.map((r) => r.machine_name || r.name).filter(Boolean);

  return normalizeAnalyzeResult(
    {
      ...combined,
      name: combined.name || "Combined load",
      badge: combined.badge || `${machineNames.length} machine${machineNames.length !== 1 ? "s" : ""}`,
      machineNames,
      isCombined: true,
    },
    { isCombined: true },
  );
}

/**
 * Fetch hierarchy scope from local enviz_api GET /api/off-grid-calculator/scope
 */
export async function fetchOffGridScope(username, branch = ALL_BRANCHES, mainSlot = "all") {
  const params = { username };
  const normalizedBranch = normalizeBranch(branch);
  if (normalizedBranch) params.branch = normalizedBranch;
  if (mainSlot && mainSlot !== "all") params.main_slot = mainSlot;

  const response = await api.get(`${BASE}/scope`, { params });
  return normalizeScopeResponse(response.data);
}

/**
 * Run load analysis + solar sizing via POST /api/off-grid-calculator/analyze
 */
export async function analyzeOffGridSizing(payload) {
  const body = {
    username: payload.username,
    mode: payload.mode,
    sizing_case: payload.sizingCase ?? "worst",
    combine: payload.combine ?? true,
    machine_names: payload.machineNames ?? [],
  };

  const normalizedBranch = normalizeBranch(payload.branch);
  if (normalizedBranch) body.branch = normalizedBranch;
  if (payload.mainSlot) body.main_slot = payload.mainSlot;

  const response = await api.post(`${BASE}/analyze`, body);
  const data = response.data?.data ?? response.data ?? {};

  const results = (data.results || [])
    .map((row) => normalizeAnalyzeResult(row))
    .filter(Boolean);

  const combined = normalizeCombinedResult(data.combined, data.results || []);

  return {
    dateRange: data.dateRange,
    results,
    combined,
    empty: Boolean(data.empty ?? (!combined && results.length === 0)),
    message: data.message || null,
    scope: data.scope || null,
  };
}

/**
 * An empty scope is a normal state, not a failure. Older API builds report it
 * as a 4xx instead of the graceful `empty: true` payload, so treat both alike.
 */
export function isEmptyScopeError(err) {
  const status = err?.response?.status ?? err?.status ?? err?.parsedError?.status;
  if (status !== 400 && status !== 404) return false;

  const detail =
    err?.response?.data?.detail ??
    err?.response?.data?.message ??
    err?.parsedError?.message ??
    err?.message ??
    "";
  return /no machines?\b/i.test(String(detail));
}

export { hasLoadData };
